import { useState } from "react";
import { Loader2, Undo2 } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useRefundCreditLotMutation } from "@/hooks/mutations/useRefundCreditLot";

interface RefundCreditLotDialogProps {
  partyId: number;
  lotId: number;
  remainingAmount: number;
}

// manager+ (route-gated server-side, same bar as the épargne withdrawal
// override) — refunds cash back to the client on an open lot. The backend
// mechanism already existed; this is its first UI entry point (2026-09-23).
export function RefundCreditLotDialog({ partyId, lotId, remainingAmount }: RefundCreditLotDialogProps) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(0);
  const refundMutation = useRefundCreditLotMutation(partyId);

  function reset() {
    setAmount(0);
  }

  function handleSubmit() {
    refundMutation.mutate(
      { lotId, amount },
      {
        onSuccess: () => {
          setOpen(false);
          reset();
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
        else setAmount(remainingAmount);
      }}
    >
      <DialogTrigger>
        <Button size="sm" variant="outline">
          <Undo2 size={13} /> Rembourser
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rembourser ce lot de crédit</DialogTitle>
          <DialogDescription>
            Montant remboursé au client — maximum {remainingAmount.toLocaleString("fr-FR")} XAF.
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="block text-[11.5px] font-medium text-body mb-1.5">Montant</label>
          <Input
            type="number"
            value={amount || ""}
            onChange={(e) =>
              setAmount(Math.min(Math.max(parseFloat(e.target.value) || 0, 0), remainingAmount))
            }
            max={remainingAmount}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={refundMutation.isPending || amount <= 0 || amount > remainingAmount}
          >
            {refundMutation.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              "Confirmer le remboursement"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
